import { getEnv, types } from "mobx-state-tree"
import { endpoints } from "../api/ApiEndpoints"
import { api } from "../api/ApiService"
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage"
import { Column, ColumnType, DisplayValues } from "../../components/datatable/interfaces"
import { reaction } from "mobx"
import { organizationSubscriptionService } from "../organization/OrganizationSubscription"

export enum ActivityTypes {
    VideoAnalysis = 1,
    HighlightDetection = 2,
    VideoConversion = 3,
    VideoOcr = 4,
    VideoAnalysisAndSegmentation = 5
}

export function getActivityLabel(activity: ActivityTypes[]) {
    if (activity.filter(a => a === ActivityTypes.VideoAnalysis || ActivityTypes.HighlightDetection).length === 2) return "general.analysis-and-segmentation";
    if (activity.filter(a => a === ActivityTypes.VideoConversion).length === 1) return "general.video-conversion";
    if (activity.filter(a => a === ActivityTypes.VideoAnalysis).length === 1) return "general.video-analysis";
    if (activity.filter(a => a === ActivityTypes.HighlightDetection).length === 1) return "general.video-segmentation";
    if (activity.filter(a => a === ActivityTypes.VideoOcr).length === 1) return "general.inspection-insights";
    return ""
}

export function getAppActivity(activities: ActivityTypes[]) {
    if (activities.filter(a => a === ActivityTypes.VideoAnalysis || ActivityTypes.HighlightDetection).length === 2) return ActivityTypes.VideoAnalysisAndSegmentation;
    return activities[0];
}

export interface ActivityPrices {
    id: ActivityTypes;
    service: string;
    credits: number;
    coverage: number;
}

export const emptyActivityPrices: ActivityPrices = {
    id: 1,
    service: "",
    credits: 0,
    coverage: 0
}

const columns: Column[] = [
    {
        key: "service",
        name: "shop.activity-prices.app",
        type: ColumnType.String
    },
    {
        key: "credits",
        name: "shop.activity-prices.credits",
        type: ColumnType.Number
    },
    {
        key: "coverage",
        name: "shop.activity-prices.coverage",
        type: ColumnType.Number
    }
]

const ActivityPricesModel = types.model({
    prices: types.optional(types.frozen<ActivityPrices[]>(), [emptyActivityPrices])
})

export const ActivityPricesStorageModel = types
    .compose(ActivityPricesModel, DataGridStorageModel)
    .named("ActivityPricesStorageModel")
    .actions((self) => {
        return {
            mapResponse(response: ActivityPrices[]) {
                self.prices = response;
                self.gridColumns = columns;
                self.data = {
                    totalCount: response.length,
                    items: response.map(self.mapItem)
                }
            },
            mapItem(item: ActivityPrices) {
                var displayValues : DisplayValues = {
                    id: {value: item.id, toDisplay: item.id},
                    service: {value: item.service, toDisplay: item.service},
                    credits: {value: item.credits, toDisplay: item.credits},
                    coverage: {value: 0, toDisplay: 0}
                }
                return {
                    displayValues: displayValues,
                    id: item.id
                }
            },
            getPrice(service:ActivityTypes) {
                return self.prices.find(p => p.id === service)?.credits || 0;
            },
            getPrices(services: ActivityTypes[]) {
                if (services.length === 1) return this.getPrice(services[0]);
                else return this.getPrice(getAppActivity(services));
            },
            updateCoverage(coins: number) {
                if (self.data === undefined) return;
                self.data.items.forEach((item: any, index: number) => {
                    var suffix = index < self.data.items.length - 1 ? " .. OR" : "";
                    var coverage = Math.floor(coins / self.data.items[index].displayValues.credits.value) + suffix;
                    self.data.items[index].displayValues.coverage.value = coverage;
                    self.data.items[index].displayValues.coverage.toDisplay = coverage;
                })
                self.setData({...self.data});
            },
            setItem(item: ActivityPrices) {
                var index = self.data.items.findIndex((i: any) => i.id === item.id);
                self.data.items[index].displayValues = {
                    id: {value: item.id, toDisplay: item.id},
                    service: {value: item.service, toDisplay: item.service},
                    credits: {value: item.credits, toDisplay: item.credits},
                    coverage: {value: item.coverage, toDisplay: item.coverage}
                }
            }
        }
    })
    .views((self) => ({
        get endpoint() {
            return endpoints.get("get_activity_prices");
        },
        get isLoading() {
            return self.prices.length === 0;
        },
        get canLoadItems() {
            return getEnv(self).organizationSubscriptionService.key;
        },
        get analysisPrice() {
            return self.prices.find(p => p.id === ActivityTypes.VideoAnalysis)?.credits || 0;
        },
        get conversionPrice() {
            return self.prices.find(p => p.id === ActivityTypes.VideoConversion)?.credits || 0;
        }
    }))

export const activityPricesStorage = ActivityPricesStorageModel.create({noPagination: true, showTotalAmount: false}, { api, organizationSubscriptionService });

reaction(
    () => organizationSubscriptionService.key,
    async () => {
        await activityPricesStorage.load();
    }
);
